/** @file useContextMenuGuard: no browser menu where ours applies; a right-drag opens none. */
import { useEffect } from 'react';
import { isTypingTarget } from '@zilo/pose3d-shortcuts/isTypingTarget';

/** A right-button press that moved more than this many pixels before release was a drag (orbit / pan), not a click. */
const DRAG_PX = 4;

/** Anything inside this keeps the browser menu (a page can opt out with the attribute). */
const NATIVE = '[data-native-menu]';

/**
 * A capture-phase `contextmenu` listener on `document`: the browser's menu is prevented everywhere except in text
 * fields (copy / paste) and `[data-native-menu]` areas. A right press that travelled is a drag, so the event is also
 * stopped before it reaches the viewport's own handler: no menu after a right-drag.
 */
export function useContextMenuGuard(): void {
  useEffect(() => {
    let down: { x: number; y: number } | null = null;
    const press = (e: PointerEvent): void => { down = e.button === 2 ? { x: e.clientX, y: e.clientY } : null; };
    const guard = (e: MouseEvent): void => {
      if (isTypingTarget(e.target) || (e.target instanceof Element && e.target.closest(NATIVE) !== null)) return;
      e.preventDefault();
      const moved = down !== null && Math.hypot(e.clientX - down.x, e.clientY - down.y) > DRAG_PX;
      down = null;
      if (moved) e.stopPropagation();
    };
    document.addEventListener('pointerdown', press, true);
    document.addEventListener('contextmenu', guard, true);
    return () => {
      document.removeEventListener('pointerdown', press, true);
      document.removeEventListener('contextmenu', guard, true);
    };
  }, []);
}
